import { ReactNode, useMemo } from 'react';
import { Descendant, Text } from 'slate';
import TemplateEditor from 'components/TemplateEditor';
import conceptPlugin from 'components/TemplateEditor/lySlatePlugins/conceptPlugin';
import dividingLinePlugin from 'components/TemplateEditor/lySlatePlugins/dividingLinePlugin';
import ConceptMenu from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import DividingLineButton from 'components/TemplateEditor/toolbarPlugins/DividingLineButton';
import { css } from '@emotion/css';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { modeTranslate } from 'services/api/overseaTranslate';
import { ConceptElement, ImageElement, ParagraphElement } from './slateTypes';

const Default: Descendant[] = [
  {
    type: CustomSlateNodeType.PARAGRAPH,
    children: [
      {
        text: '',
      },
    ],
  },
];
export interface Concept {
  id: Marketing.Concept;
  label: string;
}

const styles = {
  container: css`
    position: relative;
  `,
  countOfText: css`
    position: absolute;
    right: 8px;
    top: 12px;
  `,
};

const plugins = [conceptPlugin, dividingLinePlugin];

const conceptOccupiedLengthMap = {
  缺货商品名称: 30,
  推荐商品说明: 150,
};

export const getConceptOccupiedLength = (concept: ConceptElement) => {
  const { label } = concept;

  const { [label as keyof typeof conceptOccupiedLengthMap]: conceptOccupiedLength = 20 } =
    conceptOccupiedLengthMap;

  return conceptOccupiedLength;
};

export function getSegmentsLength(
  descendants: Descendant[] = [],
  type: 'all' | 'text' | 'image' = 'all',
): number {
  try {
    return descendants.reduce(
      (res, element) => {
        if (element.type === CustomSlateNodeType.DIVIDING_LINE) {
          res.cur = 0;

          return res;
        }

        const add = (num: number) => {
          res.cur += num;

          res.max = Math.max(res.cur, res.max);
        };

        if (Text.isText(element)) {
          if (type === 'all' || type === 'text') {
            add(element.text.length);
          }

          return res;
        }

        if (element.type === CustomSlateNodeType.CONCEPT) {
          if (type === 'all' || type === 'text') {
            add(getConceptOccupiedLength(element));
          }

          return res;
        }

        if (
          element.type === CustomSlateNodeType.ITEM ||
          element.type === CustomSlateNodeType.LOGISTICS
        ) {
          if (type !== 'text') {
            add(1);
          }

          return res;
        }

        if (element.type === CustomSlateNodeType.IMAGE) {
          if (type === 'text') {
            return res;
          }

          if (element.children?.[0]?.text === 'follow') {
            add(1);

            return res;
          }

          let num = 0;
          element.images.forEach((v) => {
            if (v.imageId) {
              num += 1;
            }
          });

          add(num);

          return res;
        }

        if (element.type === CustomSlateNodeType.PARAGRAPH) {
          if (type === 'all' || type === 'text') {
            add(getSegmentsLength(element.children));
          }

          return res;
        }

        return res;
      },
      {
        max: 0,
        cur: 0,
      },
    ).max;
  } catch (err) {
    return 0;
  }
}

export function hasConcept(descendants: Descendant[] = [], concept: Marketing.Concept): boolean {
  return descendants.some((segment) => {
    if (segment.type === CustomSlateNodeType.CONCEPT) {
      return segment.label === concept;
    }

    if (segment.type === CustomSlateNodeType.PARAGRAPH) {
      return hasConcept(segment.children, concept);
    }

    return false;
  });
}

export function getTranslateSegements<T extends Descendant | ParagraphElement>(
  segments: T[],
  to: General.Language,
): Promise<T[]> {
  return segments.reduce<Promise<T[]>>((promiseRes, element) => {
    return promiseRes.then((res) => {
      if (Text.isText(element) && element.text && element.text.trim()) {
        return modeTranslate(element.text, to, 'GOOGLE').then((text) => {
          res.push({
            ...element,
            text,
          });

          return res;
        });
      }

      if (element.type === CustomSlateNodeType.PARAGRAPH) {
        return getTranslateSegements((element as ParagraphElement).children, to).then(
          (children) => {
            res.push({
              ...element,
              children,
            });

            return res;
          },
        );
      }

      if (element.type === CustomSlateNodeType.IMAGE) {
        const { images = [], ...others } = element as ImageElement;

        const succeedImages = images.filter((image) => image.imageId);

        if (succeedImages.length === 0) {
          return res;
        }

        const newElement = {
          ...others,
          images: succeedImages,
        } as T;

        res.push(newElement);

        return res;
      }

      res.push(element);

      return res;
    });
  }, Promise.resolve([]));
}

export interface EditorProps {
  concepts: Concept[];
  value?: Descendant[];
  onChange: (value: Descendant[]) => void;
  readOnly?: boolean;
  limit?: number;
  currentCount?: number;
  children?: ReactNode;
  storeId?: string;
}

export const Editor = (props: EditorProps) => {
  const { value, onChange, concepts, readOnly, limit, currentCount, children, storeId } = props;

  return (
    <div className={styles.container}>
      <TemplateEditor
        lySlatePlugins={plugins}
        toolbarChildren={useMemo(
          () => (
            <>
              <ConceptMenu concepts={concepts} />
              <DividingLineButton />
            </>
          ),
          [concepts],
        )}
        value={value || Default}
        onChange={onChange}
        readOnly={readOnly}
        useFooterPlugin
        storeId={storeId}
      >
        {children}
      </TemplateEditor>
      {typeof currentCount === 'number' && limit && !readOnly ? (
        <span className={styles.countOfText}>{`${currentCount}/${limit}`}</span>
      ) : null}
    </div>
  );
};

export default Editor;
